import { useVideoExportContext } from "@/animation/video-generator/components/VideoExportContext";
import {
  SnapshotLabelHtmlClassNames,
  useToolSnapshotConstructor,
} from "@/animation/video-generator/generator/snapshotGenerator";
import { createTimeFramesFromState } from "@/animation/video-generator/utils/videoUtils";
import { allScenesLoaded } from "@/models/time-control/time-scene";
import { TimeEventType } from "@/models/time-control/timeStateEvents";
import { useCurrTimeState, useTimeService } from "@/models/time-control/timeStateHooks";
import { type TimeStateMachine, TimeStates } from "@/models/time-control/timeStateTypes";
import {
  type AnimationExportRunOutcome,
  recordAnimationExportFinalizeMs,
  recordAnimationExportFrameBackpressureMs,
  recordAnimationExportFrameCaptureMs,
  recordAnimationExportFrameProcessingMs,
  recordAnimationExportFrameWaitReadyMs,
  recordAnimationExportFrameWorkerMs,
  recordAnimationExportRunFrames,
  recordAnimationExportRunProcessingMs,
  recordAnimationExportRunsTotal,
} from "@/otel/metrics";
import { useMainContentRect } from "@/overlay/components/FileWindow/utils/useMainContentRect";
import { mediaExportThreadPool } from "@/threads/MediaExportThread/MediaExportThreadPool";
import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import type { InterpreterFrom } from "xstate";

// Currently frameGenerator has very weak error handling
// Failed frame most likely will get skipped or hang UI
// TODO Implement more robust error handling

// Upper bound for frames concurrently being pushed to the video worker.
// This keeps memory pressure and message queue growth bounded while still
// allowing small overlap between capture and worker-side composition/encode.
const MAX_IN_FLIGHT_PUSH_FRAMES = 2;
const FIRST_FRAME_UI_SETTLE_PAINTS = 2;

type ExportPushState = {
  inFlight: Set<Promise<void>>;
};

type ToolCountBucket = "1" | "2" | "3" | "4" | "6" | "other";
type FrameUniqueness = "all_unique" | "has_repeats";
type MetricAttributes = Record<string, string | number | boolean>;

type ExportMetricsState = {
  baseAttributes: Omit<MetricAttributes, "tool_count">;
  toolCount: ToolCountBucket;
  totalFrameProcessingMs: number;
  completedFrameCount: number;
  hadFramePushError: boolean;
};

function getMetricAttributes(metricsState: ExportMetricsState): MetricAttributes {
  return {
    ...metricsState.baseAttributes,
    tool_count: metricsState.toolCount,
  };
}

function normalizeToolCount(toolCount: number): ToolCountBucket {
  switch (toolCount) {
    case 1:
      return "1";
    case 2:
      return "2";
    case 3:
      return "3";
    case 4:
      return "4";
    case 6:
      return "6";
    default:
      return "other";
  }
}

function maybeUpdateToolCount(metricsState: ExportMetricsState, toolCount: number) {
  if (metricsState.toolCount !== "other") {
    return;
  }
  metricsState.toolCount = normalizeToolCount(toolCount);
}

function getViewportBucket(width: number, height: number): string {
  const area = Math.max(1, width) * Math.max(1, height);
  if (area <= 1280 * 720) {
    return "<=720p";
  }
  if (area <= 1920 * 1080) {
    return "<=1080p";
  }
  if (area <= 2560 * 1440) {
    return "<=1440p";
  }
  return ">1440p";
}

function getViewportAreaBucket(width: number, height: number): string {
  const area = Math.max(1, width) * Math.max(1, height);
  if (area <= 1_000_000) {
    return "small";
  }
  if (area <= 2_500_000) {
    return "medium";
  }
  return "large";
}

function getDprBucket(dpr: number): string {
  if (!Number.isFinite(dpr) || dpr <= 1) {
    return "1";
  }
  if (dpr <= 2) {
    return "2";
  }
  return ">=3";
}

function getTargetFpsBucket(targetFPS: number): string {
  if (targetFPS <= 5) {
    return "1-5";
  }
  if (targetFPS <= 12) {
    return "6-12";
  }
  if (targetFPS <= 24) {
    return "13-24";
  }
  if (targetFPS <= 30) {
    return "25-30";
  }
  return ">30";
}

function getTemporalResolutionBucket(temporalResolutionMs: number): string {
  if (!Number.isFinite(temporalResolutionMs) || temporalResolutionMs <= 60_000) {
    return "<=1m";
  }
  if (temporalResolutionMs <= 5 * 60_000) {
    return ">1m-5m";
  }
  if (temporalResolutionMs <= 15 * 60_000) {
    return ">5m-15m";
  }
  if (temporalResolutionMs <= 60 * 60_000) {
    return ">15m-1h";
  }
  return ">1h";
}

function getFrameCountBucket(frameCount: number): string {
  if (frameCount <= 30) {
    return "<=30";
  }
  if (frameCount <= 100) {
    return "31-100";
  }
  if (frameCount <= 300) {
    return "101-300";
  }
  return ">300";
}

function getFrameUniqueness(timeList: DateTime[]): FrameUniqueness {
  const uniqueTimes = new Set(timeList.map((time) => time.toMillis()));
  return uniqueTimes.size === timeList.length ? "all_unique" : "has_repeats";
}

function createExportMetricsState(args: {
  widthLogicalPx: number;
  heightLogicalPx: number;
  devicePixelRatio: number;
  targetFPS: number;
  temporalResolutionMs: number;
  frameCount: number;
  frameUniqueness: FrameUniqueness;
  queueScheduleMode: "idle" | "immediate";
}): ExportMetricsState {
  const baseAttributes: Omit<MetricAttributes, "tool_count"> = {
    viewport_bucket: getViewportBucket(args.widthLogicalPx, args.heightLogicalPx),
    viewport_area_bucket: getViewportAreaBucket(args.widthLogicalPx, args.heightLogicalPx),
    dpr_bucket: getDprBucket(args.devicePixelRatio),
    target_fps_bucket: getTargetFpsBucket(args.targetFPS),
    temporal_resolution_bucket: getTemporalResolutionBucket(args.temporalResolutionMs),
    frame_count_bucket: getFrameCountBucket(args.frameCount),
    frame_uniqueness: args.frameUniqueness,
    queue_schedule_mode: args.queueScheduleMode,
  };
  return {
    baseAttributes,
    toolCount: "other",
    totalFrameProcessingMs: 0,
    completedFrameCount: 0,
    hadFramePushError: false,
  };
}

function waitForNextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

async function waitForUiSettledPaints(paintCount: number): Promise<void> {
  for (let i = 0; i < paintCount; i += 1) {
    await waitForNextAnimationFrame();
  }
}

export function useVideoGenerator(tabId: number) {
  const { frameQueue, frameRecordingQueueStatus, isVideoRecordingActive } = useVideoExportContext();
  const { snapshotActiveToolsAsync, snapshotToolLabelOverlaysAsync } = useToolSnapshotConstructor(tabId);
  const mainRect = useMainContentRect();

  const timeState = useCurrTimeState();
  const { send } = useTimeService();
  const timeList = useMemo<DateTime[]>(() => createTimeFramesFromState(timeState), [timeState]);
  /**
   * Generate callback to construct the UI snapshot at a given display time.
   */
  const createFrameTask = useCallback(
    (
      time: DateTime,
      send: InterpreterFrom<TimeStateMachine>["send"],
      pushState: ExportPushState,
      metricsState: ExportMetricsState,
    ) => {
      // You can think of this callback as "tasks" that get queued in the frame queue.
      return async (index: number, totalTaskNum: number) => {
        // Set the current UI to the given time
        send({
          type: TimeEventType.updateFullAnimationConfig,
          displayTime: time,
          // During export we only need the current frame fully ready.
          // Keeping nextDisplayTime equal avoids unnecessary prefetch/rebuffer churn.
          nextDisplayTime: time,
        });

        // Wait until everything in UI is loaded.
        const isLastFrame = index + 1 === totalTaskNum;
        const waitReadyStarted = performance.now();
        await allScenesLoaded(time, isLastFrame, {
          checkNextFrameReadiness: false,
          onlyActiveScenes: true,
          waitForMapRender: false,
          resolveReadyLayersOnAnimationFrame: false,
        });

        // Frame 0 is most susceptible to capturing pre-export UI.
        // Wait for export-mode state/style updates to complete at least one paint cycle.
        if (index === 0) {
          await waitForUiSettledPaints(FIRST_FRAME_UI_SETTLE_PAINTS);
        }

        recordAnimationExportFrameWaitReadyMs({
          durationMs: performance.now() - waitReadyStarted,
          attributes: getMetricAttributes(metricsState),
        });

        const frameProcessingStarted = performance.now();

        // Non-map tools are captured as full cell composites (content + label overlays).
        // We only need a separate overlay capture for maps (legends/labels on top of canvas snapshot).
        const captureStarted = performance.now();
        const [toolSnaps, labelSnaps] = await Promise.all([
          snapshotActiveToolsAsync(),
          snapshotToolLabelOverlaysAsync(SnapshotLabelHtmlClassNames.Map),
        ]);

        const offsetX = mainRect?.left ?? 0;
        const offsetY = mainRect?.top ?? 0;

        // Adjust rects relative to main content
        const adjustedTools = toolSnaps.map((s) => {
          const r = s.rect;
          const adjustedRect = new DOMRect(r.left - offsetX, r.top - offsetY, r.width, r.height);
          return { ...s, rect: adjustedRect };
        });
        const adjustedLabels = labelSnaps.map((s) => {
          const r = s.rect;
          const adjustedRect = new DOMRect(r.left - offsetX, r.top - offsetY, r.width, r.height);
          return { ...s, rect: adjustedRect };
        });
        maybeUpdateToolCount(metricsState, adjustedTools.length);

        recordAnimationExportFrameCaptureMs({
          durationMs: performance.now() - captureStarted,
          attributes: getMetricAttributes(metricsState),
        });

        // Send a single ordered snapshot list to the worker and let it do one
        // composition pass per frame: base tool snapshots first, map overlays
        // (labels/legends) on top. This avoids extra main-thread canvas passes
        // and repeated encode/decode churn.
        const orderedSnapshots = [...adjustedTools, ...adjustedLabels];

        const backpressureStarted = performance.now();
        while (pushState.inFlight.size >= MAX_IN_FLIGHT_PUSH_FRAMES) {
          await Promise.race(pushState.inFlight);
        }
        recordAnimationExportFrameBackpressureMs({
          durationMs: performance.now() - backpressureStarted,
          attributes: getMetricAttributes(metricsState),
        });

        const pushStarted = performance.now();
        const trackedPushPromise = mediaExportThreadPool
          .pushFrame(orderedSnapshots)
          .then(() => {
            const workerMs = performance.now() - pushStarted;
            recordAnimationExportFrameWorkerMs({
              durationMs: workerMs,
              attributes: {
                ...getMetricAttributes(metricsState),
                frame_outcome: "success",
              },
            });

            const frameProcessingMs = performance.now() - frameProcessingStarted;
            recordAnimationExportFrameProcessingMs({
              durationMs: frameProcessingMs,
              attributes: getMetricAttributes(metricsState),
            });
            metricsState.totalFrameProcessingMs += frameProcessingMs;
            metricsState.completedFrameCount += 1;
          })
          .catch((error) => {
            metricsState.hadFramePushError = true;
            recordAnimationExportFrameWorkerMs({
              durationMs: performance.now() - pushStarted,
              attributes: {
                ...getMetricAttributes(metricsState),
                frame_outcome: "error",
              },
            });
            console.error("Failed to push video frame", error);
          });
        pushState.inFlight.add(trackedPushPromise);
        void trackedPushPromise.finally(() => {
          pushState.inFlight.delete(trackedPushPromise);
        });
      };
    },
    [snapshotActiveToolsAsync, snapshotToolLabelOverlaysAsync, mainRect],
  );

  /**
   * A callback that generates frame queue and start executing the snapshot taking process.
   */
  const recordUIVideo = useCallback(
    async (targetFPS: number) => {
      if (!frameQueue) {
        throw Error("Frame recording queue not found");
      }

      const widthLogicalPx = Math.round(mainRect?.width ?? window.innerWidth);
      const heightLogicalPx = Math.round(mainRect?.height ?? window.innerHeight);
      const devicePixelRatio = window.devicePixelRatio;
      const temporalResolutionMs = timeState.matches(TimeStates.ANIMATION)
        ? timeState.context.modeOpts.temporalResolution.valueOf()
        : 0;
      const metricsState = createExportMetricsState({
        widthLogicalPx,
        heightLogicalPx,
        devicePixelRatio,
        targetFPS,
        temporalResolutionMs,
        frameCount: timeList.length,
        frameUniqueness: getFrameUniqueness(timeList),
        queueScheduleMode: frameQueue.scheduleMode,
      });
      let runOutcome: AnimationExportRunOutcome = "success";
      let finalizeMs = 0;
      const pushState: ExportPushState = {
        inFlight: new Set<Promise<void>>(),
      };
      try {
        await mediaExportThreadPool.createVideo(
          {
            widthLogicalPx,
            heightLogicalPx,
            devicePixelRatio,
          },
          targetFPS,
        );

        // Create a queue for each time step frame
        // Each task within the queue puts the snapshots to the video thread pool
        const frameTasks = timeList.map((time) => createFrameTask(time, send, pushState, metricsState));
        frameQueue.createQueue(frameTasks);
        const tasks = await frameQueue.process();

        if (tasks.wasCanceled) {
          runOutcome = "canceled";
          // Return null video, if the recording has been cancelled in the middle.
          return null;
        }

        if (pushState.inFlight.size > 0) {
          await Promise.allSettled(Array.from(pushState.inFlight));
        }

        const finalizeStarted = performance.now();
        // Finalize the video using the taken snapshots
        const videoBlob = await mediaExportThreadPool.processVideo();
        finalizeMs = performance.now() - finalizeStarted;
        recordAnimationExportFinalizeMs({
          durationMs: finalizeMs,
          attributes: getMetricAttributes(metricsState),
        });
        return videoBlob;
      } catch (error) {
        runOutcome = "error";
        throw error;
      } finally {
        if (runOutcome === "success" && metricsState.hadFramePushError) {
          runOutcome = "error";
        }
        const attributes = getMetricAttributes(metricsState);
        recordAnimationExportRunFrames({
          frames: metricsState.completedFrameCount,
          attributes,
        });
        recordAnimationExportRunProcessingMs({
          durationMs: metricsState.totalFrameProcessingMs + finalizeMs,
          attributes,
        });
        recordAnimationExportRunsTotal({
          outcome: runOutcome,
          attributes,
        });
      }
    },
    [frameQueue, timeList, createFrameTask, send, mainRect, timeState],
  );

  const cancelUIRecording = () => {
    frameQueue?.cancel();
    // IMPORTANT:
    // Make sure to call disposeVideo() here, as it cleans up the open resources such as video encoder.
    // Failure to do so can cause memory leak.
    void mediaExportThreadPool.disposeVideo().catch((error) => {
      console.error("Failed to dispose video export resources", error);
    });
  };

  return {
    recordUIVideo,
    cancelUIRecording,
    status: frameRecordingQueueStatus,
    isActive: isVideoRecordingActive,
  };
}
